import { Head, Link, router, useForm } from "@inertiajs/react";
import { ArrowLeft, UserCheck } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import AccountBadge from "@/components/admin/AccountBadge";
import CampaignPlanFields, { FormError, tierPriceDollars } from "@/components/admin/CampaignPlanFields";
import SignupDetails from "@/components/admin/SignupDetails";
import CampaignReceipts from "@/components/admin/CampaignReceipts";
import { formatDate, formatMoney, pluralize } from "@/lib/format";
import StatusBadge from "@/components/shared/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export default function Show({ school, application, campaigns, tiers, receipt_recipients, receipt_default_recipient_id, payment_methods, today, back_path }) {
  const toggleFree = () => {
    const next = !school.complimentary;
    const message = next
      ? `Make ${school.name} a free school? Every campaign is approved, with no fees or student limits.`
      : `Make ${school.name} pay per campaign? Campaigns that already ran while it was free keep going on a $0 plan. Drafts will need their fee paid before they go live.`;
    if (!confirm(message)) return;
    router.patch(school.update_path, { complimentary: next }, { preserveScroll: true });
  };

  return (
    <AdminLayout
      eyebrow={[school.city, school.province].filter(Boolean).join(", ") || "School"}
      title={school.name}
      actions={
        <Button variant="outline" size="sm" asChild>
          <Link href={back_path}>
            <ArrowLeft className="h-4 w-4" />
            All schools
          </Link>
        </Button>
      }
    >
      <Head title={`Admin · ${school.name}`} />

      <div className="flex flex-wrap items-center gap-3 mb-6 text-sm">
        <AccountBadge status={school.account_status} />
        {school.status === "archived" && <StatusBadge status={school.status} />}
        {school.impersonation && (
          <Button variant="outline" size="sm" onClick={() => router.post(school.impersonation.path)}>
            <UserCheck className="h-4 w-4" /> Impersonate {school.impersonation.name}
          </Button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Campaigns</CardTitle>
            <CardDescription>
              {school.complimentary
                ? "Free school: every campaign is approved, with no plans or student limits."
                : "Approve a campaign by recording its paid plan. Until then it can't go live, take donations, add teachers, or send email."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {campaigns.length === 0 ? (
              <p className="text-sm text-muted-foreground">No campaigns yet.</p>
            ) : (
              campaigns.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  tiers={tiers}
                  complimentary={school.complimentary}
                  receiptOptions={{ recipients: receipt_recipients, defaultRecipientId: receipt_default_recipient_id, paymentMethods: payment_methods, today }}
                />
              ))
            )}
          </CardContent>
        </Card>

        <div className="space-y-5 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle>{school.complimentary ? "Free school" : "Pays per campaign"}</CardTitle>
              <CardDescription>
                {school.complimentary
                  ? "Every campaign is approved, with no fees or student limits."
                  : "Each campaign needs its fee paid and a plan recorded here before it can go live."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" size="sm" onClick={toggleFree}>
                {school.complimentary ? "Switch to paid per campaign" : "Make it a free school"}
              </Button>
            </CardContent>
          </Card>
          {application ? <SignupDetails application={application} /> : (
            <p className="text-xs text-muted-foreground">No signup details recorded for this school.</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function CampaignRow({ campaign, tiers, complimentary, receiptOptions }) {
  const { plan } = campaign;
  // Legacy and free plans aren't on the current price list, so an upgrade
  // starts from the smallest tier that fits.
  const onPriceList = (tier) => tiers.some((t) => t.value === tier);
  const defaultTier = onPriceList(plan?.tier) ? plan.tier : campaign.suggested_plan.tier;
  const form = useForm({
    tier: defaultTier,
    price_dollars: plan ? String(plan.price_cents / 100) : tierPriceDollars(tiers, defaultTier),
    notes: plan?.notes || "",
  });

  const save = (e) => {
    e.preventDefault();
    form.patch(campaign.plan_path, { preserveScroll: true, preserveState: "errors" });
  };

  return (
    <div className="rounded-lg border border-border p-4 space-y-4">
      <form onSubmit={save} className="space-y-4">
        <div>
          <div className="font-medium flex items-center gap-2">
            {campaign.name}
            <StatusBadge status={campaign.display_state || campaign.state} showActive />
            {!plan && !complimentary && <Badge variant="warning">Awaiting payment</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">
            {campaign.dates_confirmed ? `${formatDate(campaign.start_on)} → ${formatDate(campaign.end_on)}` : "Dates not set yet"} · {pluralize(campaign.student_count, "student")}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {plan
              ? `${plan.label} · ${formatMoney(plan.price_cents)} · recorded ${formatDate(plan.recorded_at)}${plan.recorded_by ? ` by ${plan.recorded_by}` : ""}`
              : complimentary ? "Free school." : campaign.suggested_plan.chosen_by_school
                  ? `Not approved yet. The school chose the ${campaign.suggested_plan.name} plan (${formatMoney(campaign.suggested_plan.price_cents)}) for ${pluralize(campaign.student_count, "student")}.`
                  : `Not approved yet. Based on ${pluralize(campaign.student_count, "student")} it needs the ${campaign.suggested_plan.name} plan (${formatMoney(campaign.suggested_plan.price_cents)}).`}
          </p>
          {!plan && !complimentary && campaign.start_passed && (
            <p className="text-xs text-warning-foreground mt-1">
              Its start date has passed, so it goes live as soon as it's approved (once Square and students are in). If that's too late, ask the school to move its dates first.
            </p>
          )}
        </div>
        {!complimentary && (
          <>
            <CampaignPlanFields idPrefix={`campaign-${campaign.id}`} tiers={tiers} data={form.data} setData={form.setData} />
            <FormError message={form.errors.base} />
            {(form.isDirty || !plan) && (
              <Button type="submit" size="sm" disabled={form.processing}>
                {form.processing ? "Saving…" : plan ? "Save plan" : "Approve: record payment"}
              </Button>
            )}
          </>
        )}
      </form>
      {plan && !complimentary && (plan.price_cents > 0 || campaign.receipts.length > 0) && (
        <CampaignReceipts campaign={campaign} {...receiptOptions} />
      )}
    </div>
  );
}
